import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, Save, X, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import axios from "axios";
import BackToDemoButton from "../components/BackToDemoButton";
import AdminLogin from "../components/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    industries: [],
    pricing: "",
    company_size: [],
    features: [],
    website: "",
    logo_url: "",
    rating: 4.5,
    use_cases: [],
    implementation_time: "",
    technical_requirements: ""
  });

  const categories = [
    "Customer Support & Service",
    "Marketing & Lead Generation",
    "Sales & CRM",
    "Data Analysis & Business Intelligence",
    "Content Creation & Management",
    "Operations & Workflow Automation",
    "Product Development",
    "Human Resources & Recruiting",
    "Financial Management & Accounting"
  ];

  const industries = [
    "E-commerce & Retail",
    "Healthcare & Medical",
    "Financial Services & Banking",
    "Technology & Software",
    "Manufacturing & Supply Chain",
    "Marketing & Advertising",
    "Education & Training",
    "Real Estate",
    "Legal Services",
    "Hospitality & Tourism",
    "All Industries"
  ];

  const companySizes = [
    "Solo entrepreneur (just me)",
    "2-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-1000 employees",
    "1000+ employees"
  ];

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const savedAuth = sessionStorage.getItem("adminAuth");
      if (savedAuth) {
        try {
          const response = await axios.post(`${API}/admin/verify`, {
            password: savedAuth
          });
          if (response.data.success) {
            setIsAuthenticated(true);
            fetchTools();
          } else {
            sessionStorage.removeItem("adminAuth");
            setIsAuthenticated(false);
            setLoading(false);
          }
        } catch (error) {
          sessionStorage.removeItem("adminAuth");
          setIsAuthenticated(false);
          setLoading(false);
        }
      } else {
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ai-tools`);
      setTools(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch AI tools",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleArrayInput = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(Boolean);
    setFormData({ ...formData, [field]: array });
  };

  const handleMultiSelect = (field, value) => {
    const current = formData[field];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
    setFormData({ ...formData, [field]: updated });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "",
      industries: [],
      pricing: "",
      company_size: [],
      features: [],
      website: "",
      logo_url: "",
      rating: 4.5,
      use_cases: [],
      implementation_time: "",
      technical_requirements: ""
    });
    setEditingTool(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTool) {
        await axios.put(`${API}/ai-tools/${editingTool.id}`, formData);
        toast({
          title: "Success",
          description: "AI tool updated successfully"
        });
      } else {
        await axios.post(`${API}/ai-tools`, formData);
        toast({
          title: "Success",
          description: "AI tool created successfully"
        });
      }
      resetForm();
      fetchTools();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save AI tool",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (tool) => {
    setFormData({
      name: tool.name,
      description: tool.description,
      category: tool.category,
      industries: tool.industries,
      pricing: tool.pricing,
      company_size: tool.company_size,
      features: tool.features,
      website: tool.website,
      logo_url: tool.logo_url || "",
      rating: tool.rating,
      use_cases: tool.use_cases,
      implementation_time: tool.implementation_time,
      technical_requirements: tool.technical_requirements
    });
    setEditingTool(tool);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this AI tool?")) return;
    
    try {
      await axios.delete(`${API}/ai-tools/${id}`);
      toast({
        title: "Success",
        description: "AI tool deleted successfully"
      });
      fetchTools();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete AI tool",
        variant: "destructive"
      });
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    fetchTools();
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    setIsAuthenticated(false);
    toast({
      title: "Logged out",
      description: "You have been logged out successfully"
    });
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212]">
      <BackToDemoButton />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-[#1E1E1E] border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="AI Fit Engine Logo" className="w-8 h-8" />
            <span className="text-xl sm:text-2xl font-bold">Admin Panel</span>
          </button>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </Button>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {showForm ? <X size={20} className="mr-2" /> : <Plus size={20} className="mr-2" />}
              {showForm ? "Cancel" : "Add AI Tool"}
            </Button>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-8">
        {/* Form */}
        {showForm && (
          <div className="bg-white dark:bg-[#1E1E1E] rounded-xl p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {editingTool ? "Edit AI Tool" : "Add New AI Tool"}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tool Name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., CustomerBot AI"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Website *
                  </label>
                  <Input
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleInputChange}
                    required
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  placeholder="Brief description of the AI tool"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 bg-white dark:bg-[#262626] border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pricing *
                  </label>
                  <Input
                    name="pricing"
                    value={formData.pricing}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., From $99/mo"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Industries (select multiple)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 bg-gray-50 dark:bg-[#262626] rounded-lg">
                  {industries.map(industry => (
                    <label key={industry} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.industries.includes(industry)}
                        onChange={() => handleMultiSelect('industries', industry)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{industry}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Sizes (select multiple)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 bg-gray-50 dark:bg-[#262626] rounded-lg">
                  {companySizes.map(size => (
                    <label key={size} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.company_size.includes(size)}
                        onChange={() => handleMultiSelect('company_size', size)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{size}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Features (comma-separated)
                  </label>
                  <Textarea
                    value={formData.features.join(', ')}
                    onChange={(e) => handleArrayInput('features', e.target.value)}
                    rows={3}
                    placeholder="AI Chat, Analytics, Reporting"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Use Cases (comma-separated)
                  </label>
                  <Textarea
                    value={formData.use_cases.join(', ')}
                    onChange={(e) => handleArrayInput('use_cases', e.target.value)}
                    rows={3}
                    placeholder="Customer support, Lead generation"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rating (0-5)
                  </label>
                  <Input
                    name="rating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.rating}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Implementation Time
                  </label>
                  <Input
                    name="implementation_time"
                    value={formData.implementation_time}
                    onChange={handleInputChange}
                    placeholder="e.g., 1-2 weeks"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Technical Requirements
                  </label>
                  <Input
                    name="technical_requirements"
                    value={formData.technical_requirements}
                    onChange={handleInputChange}
                    placeholder="e.g., Fully managed"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">
                  <Save size={20} className="mr-2" />
                  {editingTool ? "Update Tool" : "Create Tool"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Tools List */}
        <div className="bg-white dark:bg-[#1E1E1E] rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              AI Tools ({tools.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            </div>
          ) : tools.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No AI tools yet. Click "Add AI Tool" to get started.
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {tools.map((tool) => (
                <div key={tool.id} className="p-6 hover:bg-gray-50 dark:hover:bg-[#262626] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {tool.name}
                        </h3>
                        <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs rounded-full">
                          {tool.category}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          ⭐ {tool.rating}
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        {tool.description}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          <strong>Industries:</strong> {tool.industries.join(', ')}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">•</span>
                        <span className="text-gray-600 dark:text-gray-400">
                          <strong>Pricing:</strong> {tool.pricing}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleEdit(tool)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        onClick={() => handleDelete(tool.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
